import { DateTime } from "luxon";
import { useEffect, useMemo } from "react";
import PillarModalBody, {
  ModalMaxWidth,
} from "shared/components/pillar-modal/NewPillarModalBody";
import { usePillarModalControl } from "shared/components/pillar-modal/usePillarModalControl";
import TemplateComponent from "shared/components/template/TemplateComponent";
import { useCustomSession } from "shared/context/CustomSessionContext";
import { useHubCheckoutContext } from "hub/src/pages/checkout/context/HubCheckoutContext";

const defaultProfileUpdateRequiredTemplate = `
  <style>
    .profile-update-required-template {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: calc(var(--spacing-base) * 2);
      margin-top: calc(var(--spacing-base) * 2);
      text-align: center;
    }
  </style>
  
  <div class="profile-update-required-template" data-testid="default-profile-update-required-template">
    <p class="text-detailblock-base-medium">
      Hi <span class="text-detailblock-base-semibold">{{getDisplayNameHelper session.profile}}</span>, to continue with your payment, please update your profile information first.
    </p>
  
    <div class="button-regular-general-filled">
      {{#routerPush
        type="button"
        route=(concat "/edit-user-detail?userhubRedirect=/payment/" invoice.invoiceId)
        testid="edit-profile-button"
      }}
        Update Profile
      {{/routerPush}}
    </div>
  </div>
`;

type IsProfileUpdateRequiredProps = {
  /** ISO timestamp the member's profile was last modified */
  profileLastModifiedTimestamp?: string;
  /** invoice.additionalInfo.profileUpdateRequiredTodayGTE */
  profileUpdateRequiredTodayGTE?: string;
};

/** Checkout compares dates in Chicago time, whatever the browser's zone. */
const profileUpdateRequiredZone = "America/Chicago";

/**
 * True when the profile was last modified before profileUpdateRequiredTodayGTE.
 * A date without a time is read as midnight America/Chicago. Missing or
 * invalid values never block.
 */
const isProfileUpdateRequired = ({
  profileLastModifiedTimestamp,
  profileUpdateRequiredTodayGTE,
}: IsProfileUpdateRequiredProps): boolean => {
  if (!profileLastModifiedTimestamp || !profileUpdateRequiredTodayGTE) {
    return false;
  }
  const lastModified = DateTime.fromISO(profileLastModifiedTimestamp, {
    zone: profileUpdateRequiredZone,
  });
  const requiredOnOrAfter = DateTime.fromISO(profileUpdateRequiredTodayGTE, {
    zone: profileUpdateRequiredZone,
  });
  if (!lastModified.isValid || !requiredOnOrAfter.isValid) {
    return false;
  }
  return lastModified < requiredOnOrAfter;
};

export const HubCheckoutUpdateProfileRequired = () => {
  const session = useCustomSession();
  const { routeConfig, invoice } = useHubCheckoutContext();

  const control = usePillarModalControl();

  const profileLastModifiedTimestamp =
    session?.profile?.lastModified?.timestamp;
  const profileUpdateRequiredTodayGTE =
    invoice?.additionalInfo?.profileUpdateRequiredTodayGTE;

  const profileUpdateRequired = useMemo(
    () =>
      isProfileUpdateRequired({
        profileLastModifiedTimestamp,
        profileUpdateRequiredTodayGTE,
      }),
    [profileLastModifiedTimestamp, profileUpdateRequiredTodayGTE],
  );

  useEffect(() => {
    control.setIsOpen(profileUpdateRequired);
  }, [profileUpdateRequired]);

  return (
    <PillarModalBody
      control={{ ...control, onClose: undefined }}
      title="Profile Update Required"
      maxW={ModalMaxWidth.Sm_500}
      testid="profile-update-required-modal"
    >
      <TemplateComponent
        template={
          routeConfig?.routeParams?.profileUpdateRequiredTemplate ??
          defaultProfileUpdateRequiredTemplate
        }
        data={{ session, invoice }}
      />
    </PillarModalBody>
  );
};
